import React, { useState, useEffect } from 'react';
import Header from '../common/Header/Header';
import Button from '../common/Button/Button';
import { API_BASE } from '../../config';
import './AddRegSellerForm.css';

const AddSellerForm = ({
  showAddForm,
  newSellerFormData,
  handleNewSellerChange,
  handleAddNewSeller,
  isLoading,
  handleToggleAddForm,
  editingSeller,
  handleEditSellerChange,
  handleUpdateSeller,
  onCancel,
  onEditComplete,
  onSellerUpdated
}) => {
  const [confirmPassword, setConfirmPassword] = useState('');
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [taxInvoiceFile, setTaxInvoiceFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  
  // Local state for edit mode to ensure form controls work properly
  const [localEditData, setLocalEditData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    phoneNumber: '',
    emailID: '',
    website: '',
    city: '',
    state: '',
    pan: '',
    gst: '',
    startDate: '',
    endDate: '',
    years: 1,
    package: '',
    packageId: ''
  });

  const isEditing = !!editingSeller;

  // Helper function to construct package display string from package object
  const getPackageDisplay = (pkg) => {
    if (!pkg) return '';
    return `${pkg.detail} - ₹${pkg.price} (${pkg.year} Year${pkg.year > 1 ? 's' : ''})`;
  };

  // Helper function to extract years and package name from the package value
  const extractPackageInfo = (packageValue) => {
    if (!packageValue) return { years: 1, package: '' };
    
    // Try to parse as JSON (when package object is stored as string)
    try {
      const pkg = JSON.parse(packageValue);
      return {
        years: pkg.year || 1,
        package: pkg.detail || ''
      };
    } catch {
      // Fallback: parse from display string
      const yearMatch = packageValue.match(/\((\d+)\s*Year/i);
      const years = yearMatch ? parseInt(yearMatch[1], 10) : 1;
      
      // Extract package name - remove the price and year part
      const packageName = packageValue.replace(/\s*-\s*₹[\d,]+(\s*\(.*\))?/i, '').replace(/\s*\(\d+\s*Year[s]?\)/i, '').trim();
      
      return { years, package: packageName || packageValue };
    }
  };

  // Initialize local edit data when editing seller changes
  useEffect(() => {
    if (editingSeller) {
      setLocalEditData({
        firstName: editingSeller.firstName || '',
        lastName: editingSeller.lastName || '',
        companyName: editingSeller.companyName || '',
        phoneNumber: editingSeller.phoneNumber || '',
        emailID: editingSeller.emailID || '',
        website: editingSeller.website || '',
        city: editingSeller.city || '',
        state: editingSeller.state || '',
        pan: editingSeller.pan || '',
        gst: editingSeller.gst || '',
        startDate: editingSeller.startDate || '',
        endDate: editingSeller.endDate || '',
        years: editingSeller.years || 1,
        package: editingSeller.package || '',
        packageId: editingSeller.packageId || ''
      });
      setTaxInvoiceFile(null);
    }
  }, [editingSeller]);

  // Fetch packages for subscription dropdown
  useEffect(() => {
    if (showAddForm) {
      const fetchPackages = async () => {
        setLoadingPackages(true);
        try {
          const response = await fetch('/api/packages');
          if (response.ok) {
            const data = await response.json();
            setPackages(Array.isArray(data) ? data : []);
          }
        } catch (error) {
          console.error('Error fetching packages:', error);
          setPackages([]);
        } finally {
          setLoadingPackages(false);
        }
      };
      fetchPackages();
    }
  }, [showAddForm]);

  // Handle local edit form changes
  const handleLocalEditChange = (e) => {
    const { name, value } = e.target;
    console.log('Local edit change:', name, '=', value);
    setLocalEditData(prev => ({
      ...prev,
      [name]: value
    }));
    // Also call parent handler to keep parent state in sync
    handleEditSellerChange(e);
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setTaxInvoiceFile(file);
    }
  };

  // Upload tax invoice
  const uploadTaxInvoice = async (sellerId) => {
    if (!taxInvoiceFile) return;
    
    setUploadingInvoice(true);
    try {
      const formData = new FormData();
      formData.append('attachment', taxInvoiceFile);
      
      console.log('Uploading invoice for seller:', sellerId);
      console.log('File:', taxInvoiceFile.name, taxInvoiceFile.size, taxInvoiceFile.type);
      
      const response = await fetch(`${API_BASE}/api/vcseller/${sellerId}/tax-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      console.log('Upload response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error('Failed to upload tax invoice: ' + errorText);
      }
      
      const data = await response.json();
      console.log('Tax invoice uploaded:', data);
      return data;
    } catch (error) {
      console.error('Error uploading tax invoice:', error);
      throw error;
    } finally {
      setUploadingInvoice(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For edit mode, use local edit data
    if (isEditing) {
      console.log('Submitting edit form data:', JSON.stringify(localEditData));
      console.log('Subscription value:', localEditData.subscription);
      
      try {
        setLocalLoading(true);
        
        // First, update seller data
        const response = await fetch(`/api/vcseller/${editingSeller.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(localEditData)
        });

        const responseText = await response.text();
        
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { error: `Server error: ${responseText.substring(0, 200)}` };
        }

        if (!response.ok) {
          throw new Error(errorData.error || errorData.details || `HTTP ${response.status}: Failed to update seller`);
        }

        let updatedSeller;
        try {
          updatedSeller = JSON.parse(responseText);
        } catch {
          throw new Error('Invalid response from server');
        }

        console.log('Seller updated successfully:', updatedSeller);
        
        // If there's a new tax invoice file, upload it (optional, don't fail the whole operation)
        if (taxInvoiceFile) {
          try {
            await uploadTaxInvoice(updatedSeller.id || editingSeller.id);
            // Clear the file after successful upload
            setTaxInvoiceFile(null);
          } catch (uploadError) {
            console.warn('Invoice upload failed but seller was updated:', uploadError.message);
            // Don't throw - seller was updated successfully
          }
        }
        
        // Close form and notify parent
        if (onCancel) {
          onCancel();
        }
        if (onEditComplete) {
          onEditComplete();
        }
        if (onSellerUpdated) {
          onSellerUpdated();
        }
      } catch (error) {
        console.error('Error updating seller:', error);
        alert('Failed to update seller: ' + error.message);
      } finally {
        setLocalLoading(false);
      }
      return;
    }
    
    // Debug: Log the form data before submitting
    console.log('Submitting form data: CREATE', JSON.stringify(newSellerFormData));
    
    if (!isEditing) {
      if (newSellerFormData.passwrd !== confirmPassword) {
        alert('Passwords do not match');
        return;
      }
    }
    handleAddNewSeller(e);
  };

  if (!showAddForm) return null;

  return (
    <div className="addSeller-modal-overlay">
      <div className="addSeller-modal-content">
        <div className="addSeller-popup-header">
          <Header title={isEditing ? 'Edit Seller' : 'Add New Seller'} />
          <Button 
            onClick={onCancel || handleToggleAddForm} 
            className="close-btn top-right" 
            title="Close"
          >
            ×
          </Button>
        </div>

        <form className="addSeller-form" onSubmit={handleSubmit}>
          <div className="addSeller-form-section">
            <h4>Personal Information</h4>
            <div className="addSeller-form-row">
              <div className="addSeller-form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={isEditing ? localEditData.firstName : newSellerFormData.firstName || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
              <div className="addSeller-form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={isEditing ? localEditData.lastName : newSellerFormData.lastName || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
              <div className="addSeller-form-group">
                <label>Phone Number *</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={isEditing ? localEditData.phoneNumber : newSellerFormData.phoneNumber || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
            </div>
            <div className="addSeller-form-row">
              <div className="addSeller-form-group">
                <label>Email ID *</label>
                <input
                  type="email"
                  name="emailID"
                  value={isEditing ? localEditData.emailID : newSellerFormData.emailID || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
              {!isEditing && (
              <div className="addSeller-form-row">
                <div className="addSeller-form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    name="passwrd"
                    value={newSellerFormData.passwrd || ''}
                    onChange={handleNewSellerChange}
                    required
                  />
                </div>
                <div className="addSeller-form-group">
                  <label>Confirm Password *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}
            </div>
          </div>

          <div className="addSeller-form-section">
            <h4>Company Information</h4>
            <div className="addSeller-form-row">
              <div className="addSeller-form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="companyName"
                  value={isEditing ? localEditData.companyName : newSellerFormData.companyName || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
              <div className="addSeller-form-group">
                <label>Website *</label>
                <input
                  type="text"
                  name="website"
                  value={isEditing ? localEditData.website : newSellerFormData.website || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  placeholder="https://example.com"
                />
              </div>
            </div>
            <div className="addSeller-form-row">
              <div className="addSeller-form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={isEditing ? localEditData.city : newSellerFormData.city || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
              <div className="addSeller-form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={isEditing ? localEditData.state : newSellerFormData.state || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
            </div>
            <div className="addSeller-form-row">
              <div className="addSeller-form-group">
                <label>PAN No *</label>
                <input
                  type="text"
                  name="pan"
                  value={isEditing ? localEditData.pan : newSellerFormData.pan || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
              <div className="addSeller-form-group">
                <label>GST No *</label>
                <input
                  type="text"
                  name="gst"
                  value={isEditing ? localEditData.gst : newSellerFormData.gst || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="addSeller-form-section">
            <h4>Subscription Details</h4>
            <div className="addSeller-form-row">
              <div className="addSeller-form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={isEditing ? localEditData.startDate : newSellerFormData.startDate || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
              <div className="addSeller-form-group">
                <label>End Date *</label>
                <input
                  type="date"
                  name="endDate"
                  value={isEditing ? localEditData.endDate : newSellerFormData.endDate || ''}
                  onChange={isEditing ? handleLocalEditChange : handleNewSellerChange}
                  required
                />
              </div>
            </div>
            <div className="addSeller-form-row">
              <div className="addSeller-form-group">
                <label>Package *</label>
                <select
                  name="packageId"
                  value={isEditing ? localEditData.packageId || editingSeller.packageId || '' : newSellerFormData.packageId || ''}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedPackage = packages.find(pkg => pkg.id.toString() === selectedId);
                    
                    console.log('Package selected:', selectedId, selectedPackage);
                    
                    if (selectedPackage) {
                      const years = selectedPackage.year || 1;
                      const packageName = selectedPackage.detail || '';
                      
                      const yearsEvent = { target: { name: 'years', value: years } };
                      const packageEvent = { target: { name: 'package', value: packageName } };
                      const packageIdEvent = { target: { name: 'packageId', value: selectedId } };
                      
                      if (isEditing) {
                        handleLocalEditChange(yearsEvent);
                        handleLocalEditChange(packageEvent);
                        handleLocalEditChange(packageIdEvent);
                      } else {
                        handleNewSellerChange(yearsEvent);
                        handleNewSellerChange(packageEvent);
                        handleNewSellerChange(packageIdEvent);
                      }
                    }
                  }}
                  required
                  disabled={loadingPackages}
                >
                  <option value="">Select Package</option>
                  {loadingPackages ? (
                    <option value="">Loading...</option>
                  ) : packages.length > 0 ? (
                    packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {getPackageDisplay(pkg)}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="1">Seller Service Package - ₹42000 (1 Year)</option>
                      <option value="2">Seller Service Package - ₹70000 (2 Years)</option>
                      <option value="3">Seller Service Package - ₹950000 (3 Years)</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>

          <div className="addSeller-form-section">
            <h4>Tax Invoice (PDF)</h4>
            <div className="addSeller-form-row">
              <div className="addSeller-form-group">
                <label>Upload Tax Invoice (PDF only, max 10MB)</label>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                />
                {taxInvoiceFile && (
                  <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                    Selected: {taxInvoiceFile.name} ({(taxInvoiceFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
                {isEditing && editingSeller.taxInvoicePath && !taxInvoiceFile && (
                  <div style={{ marginTop: '5px', fontSize: '12px', color: '#28a745' }}>
                    ✓ Current invoice: {editingSeller.taxInvoiceName || 'uploaded'}
                  </div>
                )}
                {uploadingInvoice && (
                  <div style={{ marginTop: '5px', fontSize: '12px', color: '#007bff' }}>
                    Uploading invoice...
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-buttons">
            <button type="submit" disabled={isLoading || localLoading || uploadingInvoice} className="add-button">
              {isLoading || localLoading || uploadingInvoice ? 'Saving...' : (isEditing ? 'Update Seller' : 'Add Seller')}
            </button>
            <button
              type="button"
              onClick={onCancel || handleToggleAddForm}
              disabled={isLoading || localLoading || uploadingInvoice}
              className="cancel-button"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSellerForm;

